import { Component, OnInit ,HostListener, signal, OnDestroy, AfterViewInit, Output, EventEmitter, ChangeDetectorRef} from '@angular/core';
import { PolicyService } from '../shared/service/policy.service';
import { Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormControl, Validators, ValidationErrors, AbstractControl, AsyncValidatorFn, FormControl, ReactiveFormsModule} from '@angular/forms';
import { AgencyLocation } from '../shared/model/agency-location';
import { CmElement, CmHelp, CmSection, CmTooltip, ContentType } from '../shared/model/cm/managed-content.model';
import { ManagedContentService } from '../shared/service/managed-content.service';
import { fromEvent, Observable, of, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { UserSessionService } from '../shared/service/user-session.service';
import { Notice } from '../shared/model/session/notice';
import { AppInsightsLoggerService } from '../shared/service/app-insights-logger.service';

import { GroupService } from '../shared/service/group.service';
import { DeactivateGuard } from '../deactivate.guard';
import { MhsApplication } from '../shared/model/mhs-application';
import { AppInsightsApiService} from "../shared/service/app-insights-api.service";
import { ViewportScroller } from "@angular/common";
import { PagetitleService } from '../shared/service/pagetitle.service';
import { AuthGuardService } from '../shared/service/auth-guard.service';
import { COMPANY_DATA_THEME, COMPANY_ID } from '../shared/global-constants';
import { GroupEdp } from '../shared/model/group-edp';
import defaultCoverages from '../../assets/demo/coverages/defaultCoverages.json';
import { ModalService } from '../shared/service/modal.service';
import { RouterService } from '../shared/service/router.service';
import { FormUtils } from '../shared/helper/form-utils';
import { WindowRef } from '../shared/service/window.service';
import { GoogleAnalyticsService } from '../shared/service/google-analytics.service';
import { FormControlUtilitiesService } from '../shared/service/form-control-utilities.service';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { ContinueCancelComponent } from '../continue-cancel/continue-cancel.component';

@Component({
  selector: 'app-policy-number',
  standalone: true,
  imports: [ReactiveFormsModule, ContinueCancelComponent],
  templateUrl: './policy-number.component.html',
  styleUrls: ['./policy-number.component.scss'],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PolicyNumberComponent implements OnInit, DeactivateGuard, OnDestroy, AfterViewInit {
  @Output() cancelEmitter = new EventEmitter<void>();
  public display = signal("none");

  resizeObservable$: Observable<Event>;
  resizeSubscription$: Subscription;
  public formSubmittedAndSpinnerStart:boolean =false;
  public innerWidth: number = window.innerWidth;
  public policyTooltipPosition: string = 'top';
  public groupTooltipPosition: string = 'right';
  
  private cancelContent: CmHelp = this.cmService.getContentById(ContentType.HELP, "lose-data-warning");
  public modalContentHeading: string = this.cancelContent.title;
  public modalContentBody: string = FormUtils.sanitize(this.cancelContent.body);

  public evidenceRequired: boolean = true;
  public ceoiPolicyFlag: boolean = false;
  public companyId: string;
  evidenceContent: CmSection;
  ceoiPolicyFlagContent: CmSection;
  public clearButtonProps = {
    keyId: 'clear-button',
    size: 'default',
    text: 'Try a Different Policy Number',
    type: 'submit',
    variant: 'primary'
  }

  public carrier: string;
  public messageText: string;

  agencyLocations: AgencyLocation[];
  public filteredAgencyLocations = signal<AgencyLocation[]>([]);
  
  myControl = new FormControl('');

  // Form
  policyForm = new UntypedFormGroup({
    policyNumber: new UntypedFormControl(null),
    groupName: new UntypedFormControl(""),
    agencyLocation: new UntypedFormControl("")
  });

  //Display items
  policyElement: CmElement;
  locationElement: CmElement;
  grpNameElement: CmElement;
  pageLoaderElement: CmElement;
  instructions: CmSection;
  formSection: CmSection;
  pageTitle: string;
  intro: string;
  hint: string;
  policyError: string;
  systemErrorContent: Notice;
  invalidPolicyContent: Notice;
  isFormSubmitted: boolean = false;
  resetSubmitted: boolean = false;
  isValidatingPolicy: boolean;
  isValidatingMhs: boolean;
  isCustomUrl: boolean = false;
  displayHelpText: boolean = false;
  isAdminUnitInvalid = false;
  public isYi: boolean = false;
  public raiseYiRedirectModal: boolean = false;

  private redirectContent: CmHelp = this.cmService.getContentById(ContentType.HELP, "sny-policy-redirect");
  public redirectContentHeading: string = this.redirectContent.title;
  public redirectContentBody: string = this.redirectContent.body;

  policyToolTip: CmTooltip;
  policyToolTipDescription: string;
  policyToolTipTitle: string;
  grpNameToolTip: CmTooltip;
  grpNameToolTipDescription: string;
  grpNameToolTipTitle: string;
  ceoiPolicyFlagContentBodyTrim: string;

  // GA4 Custom events
  public submitPolicy = {
    action: "button_click",
    category: "policy",
    name: "submit",
    value: ''
  }

  public cancelPolicy = {
    action: "button_click",
    category: "policy",
    name: "cancel",
  }

  constructor(private router: Router,
              private policyService: PolicyService,
              private groupService: GroupService,
              private cmService: ManagedContentService,
              private userSessionService: UserSessionService,
              private appInsightsLoggerService: AppInsightsLoggerService,
              private appInsightsApiService: AppInsightsApiService,
              private googleAnalyticsService: GoogleAnalyticsService,
              private scroller: ViewportScroller,
              private pagetitleService: PagetitleService,
              private authGuardService: AuthGuardService,
              private modalService: ModalService,
              private routerService: RouterService,
              private windowService: WindowRef,
              public formControlUtilitiesService: FormControlUtilitiesService,
              private cdr: ChangeDetectorRef) { }

  private log(content: string) {
    this.appInsightsLoggerService.log('info', 'Info log from policyNumber.component', content);
  }

  ngOnInit() {
    
    this.formSubmittedAndSpinnerStart = false;
    if(this.userSessionService.data.sessionEnded){
      this.router.navigate(['/instructions']);
    }
    
    // Skip policy page when continuing from confirmation
    if (this.userSessionService.data.isContinuingFromConfirmation && this.userSessionService.data.preservedPolicyData) {
      // Restore preserved policy data
      this.userSessionService.data.policy = this.userSessionService.data.preservedPolicyData.policy;
      this.userSessionService.data.grpName = this.userSessionService.data.preservedPolicyData.grpName;
      
      // Navigate to consent page
      this.router.navigate(['/consent']);
      return;
    }
    
    this.companyId = this.userSessionService.data.companyId;
    if(this.pagetitleService){
      this.pagetitleService.setPageTitle('Policy');
    }
    if(this.routerService){
      this.routerService.setRoute(this.router.url);
    }

    this.resizeObservable$ = fromEvent(window, 'resize')
    .pipe(debounceTime(300));
    
    this.resizeObservable$.subscribe( (evt: any) => {
      this.innerWidth = window.innerWidth;
      this.changeTooltipPosition();
      // Trigger change detection after tooltip position changes
      this.cdr.detectChanges();
    });

    // Get relevant CM content
    this.policyElement = this.cmService.getContentById(ContentType.ELEMENT, "policy-number");
    this.locationElement = this.cmService.getContentById(ContentType.ELEMENT, "location");
    this.pageLoaderElement = this.cmService.getContentById(ContentType.ELEMENT, "policy-number-generic-loader");
    this.formSection = this.cmService.getContentById(ContentType.SECTION, "policy");
    this.evidenceContent = this.cmService.getContentById(ContentType.SECTION, "eeoi-evidence-not-required");
    this.ceoiPolicyFlagContent = this.cmService.getContentById(ContentType.SECTION, "do-not-apply-here");

    if(this.userSessionService.data.dataTheme === COMPANY_DATA_THEME.NM){
      this.carrier = COMPANY_DATA_THEME.NM;
      let systemError = this.cmService.getContentById(ContentType.HELP,"system-error-nm");
      this.systemErrorContent = {
        id: systemError.id,
        title: systemError.title,
        body: systemError.body
      };
    } else {
      this.carrier = COMPANY_DATA_THEME.SFG;
      let systemError = this.cmService.getContentById(ContentType.HELP,"system-error");
      this.systemErrorContent = {
        id: systemError.id,
        title: systemError.title,
        body: systemError.body
      };
    }

    let invalidPolicyError = this.cmService.getContentById(ContentType.HELP,"policy-number-invalid");
    this.invalidPolicyContent = { id: invalidPolicyError.id, title: invalidPolicyError.title, body: invalidPolicyError.body };

    // Set content
    this.pageTitle = this.formSection.title;

    this.isFormSubmitted = false;
    this.isValidatingPolicy = false;

    if(this.policyElement){
      this.policyToolTip = this.cmService.getContentById(ContentType.TOOLTIP, this.policyElement.tooltip);
      this.policyToolTipDescription = this.policyToolTip.plainText;
      this.policyToolTipTitle = this.policyToolTip.title;
    }
    if(this.policyElement){
      this.policyToolTip = this.cmService.getContentById(ContentType.TOOLTIP, this.policyElement.tooltip);
      this.policyToolTipDescription = this.policyToolTip.plainText;
      this.policyToolTipTitle = this.policyToolTip.title;
    }

    // Custom URL
    if(this.userSessionService.data.policy != null){
      this.policyForm.clearValidators();
      this.policyForm.updateValueAndValidity();
      this.isCustomUrl = true;
      this.policy.setValue(this.userSessionService.data.policy);
      if(this.userSessionService.data.grpName != null){
        this.groupName.setValue(this.userSessionService.data.grpName);
      }
      if(this.userSessionService.data.agencyLocations != null){
        this.agencyLocations = this.userSessionService.data.agencyLocations;
        this.removeAdminUnitAll();
        this.filteredAgencyLocations.update(values => {
          return [...this.agencyLocations];
        });
      }

      if(this.agencyLocations){

        this.location.setValidators(Validators.required);
        if(this.agencyLocations.length == 1){
          this.location.setValue(this.userSessionService.data.agencyLocations[0].id);
        } else {
          this.location.setValue("");
          this.location.clearValidators();
        }

        if(this.location.value === null || this.location.value === "" || this.location.value === undefined){
          this.userSessionService.data.agencyLocation = undefined;
          this.policyForm.get("agencyLocation").setErrors({required:true});
          this.policyForm.get("agencyLocation").markAsTouched();
          this.policyForm.get("agencyLocation").updateValueAndValidity();
          this.policyForm.updateValueAndValidity();
        } else {
          this.policyForm.controls.agencyLocation.setValue(this.userSessionService.data.agencyLocation);
        }
      }

      this.instructions = this.cmService.getContentById(ContentType.SECTION, "custom-policy-instructions");
      this.grpNameElement = this.cmService.getContentById(ContentType.ELEMENT, "employer-custom-form");

    } else {
      // Generic URL
      this.policy.setValidators([
        Validators.required,
        Validators.min(100000),
        Validators.max(999999),
      ]);
      this.policy.setAsyncValidators([this.validatePolicyNumber()]);

      
//  CONTROL SPINNER HERE (NOT inside validator)
  this.policy.statusChanges.subscribe(status => {
    this.formSubmittedAndSpinnerStart = status === 'PENDING';
  });

      this.grpNameElement = this.cmService.getContentById(ContentType.ELEMENT, "employer-generic-form");
    }

    if(this.grpNameElement.tooltip){
      this.displayHelpText = true;
      this.grpNameToolTip = this.cmService.getContentById(ContentType.TOOLTIP, this.grpNameElement.tooltip);
      this.grpNameToolTipDescription = this.grpNameToolTip.plainText;
      this.grpNameToolTipTitle = this.grpNameToolTip.title;
    }

   if(this.policy){
      this.policy.valueChanges.subscribe(value => {
        this.groupName.setValue("");
        this.location.setValue("");
        this.location.updateValueAndValidity();
        this.policyForm.get("agencyLocation").setErrors(null);
        this.policyForm.get("agencyLocation").markAsUntouched(); 
        this.display.set("none");
        this.policyForm.updateValueAndValidity();
      });
    }

    this.appInsightsApiService.endNavigationEvent(this.router.url);

    this.ceoiPolicyFlagContentBodyTrim = this.ceoiPolicyFlagContent.body;
    this.ceoiPolicyFlagContentBodyTrim = this.ceoiPolicyFlagContentBodyTrim.replace('<p>', "");
    this.ceoiPolicyFlagContentBodyTrim = this.ceoiPolicyFlagContentBodyTrim.replace('</p>', "");
  // end ngOnInit
  }

  ngAfterViewInit(){
    this.innerWidth = window.innerWidth;
    this.changeTooltipPosition();
    // Trigger change detection after view initialization to avoid ExpressionChangedAfterItHasBeenCheckedError
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    if (this.resizeSubscription$) {
      this.resizeSubscription$.unsubscribe();
    }
  }

  changeTooltipPosition() {
    if(this.innerWidth < 755){
      if(this.innerWidth < 482){
        this.policyTooltipPosition = 'left';
        this.groupTooltipPosition = 'left';
      } else {
        this.policyTooltipPosition = 'left';
        this.groupTooltipPosition = 'right';
      }
    } else {
      this.policyTooltipPosition = 'top';
      this.groupTooltipPosition = 'right';
    }
  }

  get policy() {return this.policyForm.get("policyNumber");}
  get groupName() {return this.policyForm.get("groupName");}
  get location() {return this.policyForm.get("agencyLocation");}

  get isFormValidating() {
    return this.isValidatingPolicy || this.isAdminUnitInvalid;
   }
   
  selectedLocation: string;

  onSelectionChange(name:any){
    const selectedOption = this.agencyLocations.find(option => option.name === name);
    this.policyForm.get("agencyLocation").setValue(selectedOption.name);
    this.agencyLocationValidator(name);
    this.display.set("none");
  }

  agencyLocationValidator(name:any){
    const selectedOption = this.agencyLocations.find(option => option.name === name);
    if(selectedOption){
      this.policyForm.get("agencyLocation").setErrors(null);
      this.policyForm.get("agencyLocation").markAsTouched();
      this.policyForm.get("agencyLocation").updateValueAndValidity();
    } else if (this.policyForm.get('agencyLocation').value != null &&
               this.policyForm.get('agencyLocation').value != "" &&
               this.policyForm.get('agencyLocation').value != undefined)   
    {
      this.policyForm.get("agencyLocation").markAsTouched();
      this.policyForm.get("agencyLocation").setErrors({inValidAgencyLocation:true});
      this.policyForm.updateValueAndValidity();
    } else {
      this.policyForm.get("agencyLocation").setErrors({required:true});
      this.policyForm.get("agencyLocation").markAsTouched();
      this.policyForm.updateValueAndValidity();
    }
  }

  onKeyUp(){
    const currentValue = this.selectedLocation;
    if(currentValue === ''){
      this.display.set("block");
      this.filteredAgencyLocations.update(values => {
        return [...this.agencyLocations];
      });
      return;
    } else {
      let tempAgencyLocations: AgencyLocation[];
      tempAgencyLocations = this.agencyLocations.filter(option => option.name.toLowerCase().includes(currentValue.toLowerCase()));
      tempAgencyLocations.sort((a, b) => a.name.localeCompare(b.name));
      this.filteredAgencyLocations.update(values => {
        return [...tempAgencyLocations];
      });
      
      if(this.filteredAgencyLocations().length==0){
        this.display.set("none");
      } else{
        this.display.set("block");
      }
      
    }
 
   }
  
  onKeyUpPolicyNumber(){
    if(this.policyForm.get('policyNumber').value == "" && this.agencyLocations.length > 0){
      this.agencyLocations = [];
    }
  }

  updateLocation(){
    if(this.agencyLocations != undefined && this.agencyLocations.length> 0){
      const selectedOptionName = this.policyForm.get("agencyLocation").value;
      const selectedOption = this.agencyLocations.find(option => option.name === selectedOptionName);

      if(selectedOption){
        this.policyForm.get("agencyLocation").setValue(selectedOption.id);
        this.policyForm.updateValueAndValidity();
      } else if(selectedOptionName != null && selectedOptionName != undefined && selectedOptionName != "" &&
        selectedOption != null && selectedOption != undefined){
        this.policyForm.get("agencyLocation").setErrors({inValidAgencyLocation:true});
        this.policyForm.updateValueAndValidity();
      }else {
        if(this.agencyLocations.length > 1 && (this.policyForm.get("agencyLocation").value != null || this.policyForm.get("agencyLocation").value != "" || this.policyForm.get("agencyLocation").value != undefined)){
          this.policyForm.get("agencyLocation").setErrors({required:true});
          this.policyForm.updateValueAndValidity();
        }
      }
    }
  }

  onSubmit() {
    this.updateLocation();
    this.policyForm.markAllAsTouched();
    this.location.markAsTouched();

    if(this.isFormValidating) {
      return;
    }

    this.isFormSubmitted = true;

    if(this.policyForm.valid) {
      let redirectedYiPolicy: boolean = false;
      if(this.policyService){
        this.policyService.redirectedYiPolicy.subscribe(msg => {
          redirectedYiPolicy = msg;
        });
      }

      if(redirectedYiPolicy){
        this.redirectContentBody = this.redirectContentBody.replace('<p>', "");
        this.redirectContentBody = this.redirectContentBody.replace('</p>', "");

        //raise the modal
        this.raiseRedirectModal();

      } else {
        // Set policy on GA4 object and send to GA
        this.submitPolicy.value = this.policy.value;
        this.googleAnalyticsService.sendCustomEvent(this.submitPolicy);

        this.userSessionService.data.policy = this.policy.value;
        this.userSessionService.data.companyId = this.userSessionService.data.edpGroupData.companyId;
        if(this.location.value === null || this.location.value === "" || this.location.value === undefined) {
          this.policyForm.get("agencyLocation").setErrors({required:true});
          this.policyForm.updateValueAndValidity();
          this.userSessionService.data.agencyLocation = undefined;
        }
        else {
          this.userSessionService.data.agencyLocation = this.location.value;
          let selectedAgencyLocation: AgencyLocation = this.agencyLocations.find(location => location.id === this.location.value);
          let selectedAgencyLocationName: string = selectedAgencyLocation ? selectedAgencyLocation.name : '';
          this.userSessionService.data.agencyLocationLabel = selectedAgencyLocationName;
        }

        this.setSessionData();

        this.router.navigate(['/consent']);

        this.resetSubmitted = true;
      }      
    }
    
    if(!this.resetSubmitted){
      this.isFormSubmitted = false;
      this.resetSubmitted = false;
    }
  }

  raiseRedirectModal(){
    this.raiseYiRedirectModal = true;
    this.modalService.showModal('prism-ui-modal-redirect-modal'); // Use the service to show the modal
  }

  validatePolicyNumber(): AsyncValidatorFn {
    return (policy: AbstractControl) : Promise<ValidationErrors | null> => {
      return new Promise<ValidationErrors | null>( (resolve, reject) => {

        if(this.policyService){
          this.policyService.getPolicyDataFor(policy.value).subscribe(
            async res => {
             
              if(res === undefined) {
                resolve({'policyError': policy.value});
                this.modalContentHeading = this.systemErrorContent.title;
                this.modalContentBody = FormUtils.sanitize(this.systemErrorContent.body);

                this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal

                this.isValidatingPolicy = false;
                this.location.setValue("");
                this.userSessionService.data.agencyLocation = undefined;
              } else {
                let edpGroup = new GroupEdp();
                edpGroup = res;
                //make sure the company ID of the group matches the expected company ID
                let result = await this.policyService.checkTheCompanyIdMatchesExpectedCompanyId(edpGroup.companyId, this.carrier);
                if(result != false){

                  let companyIdOk = this.validateCompanyIdIsCorrect(result, policy.value);

                  if(companyIdOk === true){
                    this.ceoiPolicyFlag = edpGroup.ceoiPolicy;

                    if(this.ceoiPolicyFlag){

                      if(edpGroup.groupName){
                        this.groupName.setValue(edpGroup.groupName);
                        resolve(null);
                        this.isValidatingPolicy = false;
                      }
                      
                    } 
                      else{

                      this.evidenceRequired = edpGroup.evidenceRequired;

                      if(this.evidenceRequired){
                        // set default products if none came back from edp
                        if(edpGroup.productsByApplicantType.length === 0){
                          edpGroup.productsByApplicantType = defaultCoverages.productsByApplicantType;
                        }
                        
                        this.userSessionService.data.edpGroupData = edpGroup;
                        
                        this.userSessionService.data.grpName = edpGroup.groupName;

                        //set the groupName
                        if(edpGroup.groupName){
                          this.groupName.setValue(edpGroup.groupName);
                        }

                        //load the admin units
                        this.initializeAdminUnits(edpGroup);
                        
                        resolve(null);
                        this.isValidatingPolicy = false;

                        //end of admin units code
                      }
                      else {
                      
                        let endPart: string = this.evidenceContent.body;
                        endPart = endPart.replace('<p>', "");
                        endPart = endPart.replace('</p>', "");
                        this.messageText = 'The policy #' + this.policyForm.get("policyNumber").value + " "+ endPart;
                      }
                    }
                  } else {
                    if(result != "YI"){
                      this.isValidatingPolicy = false;
                      resolve({'policyError': policy.value});
                    } else {
                      resolve(null);
                    }
                  }
                } else {
                  this.isValidatingPolicy = false;
                  this.log('Policy number validation failed for policy number: ' + policy.value + ' EDP group data is undefined.');
                  resolve({'policyError': policy.value});
                }
              }
            },
            err => {
              this.log('Invalid policy number entered: ' + policy.value + ' Error: ' + JSON.stringify(err));
              resolve({'invalidPolicyNumber': true});
            }
            );
        }

      });
    }
  }

  initializeAdminUnits(edpGroup: GroupEdp){
    this.agencyLocations = edpGroup.adminUnits;
    this.agencyLocations.sort((a, b) => a.name.localeCompare(b.name));

    //remove the admin unit for "All"
    this.removeAdminUnitAll();
    this.filteredAgencyLocations.update(values => {
      return [...this.agencyLocations];
    });

    if(edpGroup.adminUnits.length == 1) {
      this.location.setValue(edpGroup.adminUnits[0].id.toString());
    }

    // ensure validators for agency location are cleared or restored
    if(edpGroup.adminUnits.length===0) {
      this.policyForm.get("agencyLocation").setValue("");
      this.policyForm.get("agencyLocation").clearValidators();
      this.policyForm.get("agencyLocation").updateValueAndValidity();
    }
    else {
      this.location.setValidators(Validators.required);
      this.location.markAsPristine();
    }
    this.location.updateValueAndValidity();
  }

  validateCompanyIdIsCorrect(companyId: string, policyNumber: string) {
    if(companyId === COMPANY_ID.YI){//redirect to instructions and raise the modal
      if(this.policyService){
        this.policyService.isYiPolicyRedirected(true);
      }
      return "redirect";
    } else if (companyId === COMPANY_ID.SI){
      if(this.policyService){
        this.policyService.isYiPolicyRedirected(false);
      }
      if(this.userSessionService.data.dataTheme === COMPANY_DATA_THEME.SFG
        && this.userSessionService.data.companyId === COMPANY_ID.SI
        && companyId === COMPANY_ID.SI){
        return true;
      } else {
        return false;
      }
    } else {
      if(this.policyService){
        this.policyService.isYiPolicyRedirected(false);
      }
      if(this.userSessionService.data.dataTheme === COMPANY_DATA_THEME.NM
        && this.userSessionService.data.companyId === COMPANY_ID.NM
        && companyId === COMPANY_DATA_THEME.NM){//edp for some reason used "nwm" instead of "NM"
        return true;
      } else {
        return false;
      }
    }
  }

  removeAdminUnitAll(){
    let tempAgencyLocations: AgencyLocation[];
    tempAgencyLocations = this.agencyLocations.filter((element, index) => element.id != "ALL");
    this.agencyLocations = tempAgencyLocations;
    this.agencyLocations.sort((a, b) => a.name.localeCompare(b.name));
  }

  setSessionData(){
    if(this.userSessionService.data.mhsApplication === undefined){
      this.userSessionService.data.mhsApplication = new MhsApplication();
    }
    if(this.userSessionService.data.agencyLocation === undefined || this.userSessionService.data.agencyLocation === ""){
      this.userSessionService.data.mhsApplication.adminUnit = '0';
    } else {
      this.userSessionService.data.mhsApplication.adminUnit = this.userSessionService.data.agencyLocation;
    }
    this.userSessionService.data.mhsApplication.groupName = this.userSessionService.data.grpName;
    this.userSessionService.data.mhsApplication.policyNumber = this.userSessionService.data.policy.toString();
  }

  onCancel(){
    this.googleAnalyticsService.sendCustomEvent(this.cancelPolicy);
    this.isFormSubmitted = false;

    this.cancelEmitter.emit();

    this.modalContentHeading = this.cancelContent.title;
    this.modalContentBody = FormUtils.sanitize(this.cancelContent.body);

    this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal
  }

  onCancelModal(modalName: string){
    const modalElement =
      this.windowService.nativeDocument.getElementById(modalName);

    if (modalElement && modalElement.close) {
      modalElement.close();
    } else {

      this.modalContentHeading = this.systemErrorContent.title;
      this.modalContentBody = FormUtils.sanitize(this.systemErrorContent.body);

      this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal

      this.log('Modal with id prism-ui-modal-cancel-modal not found or does not support close method.');
    }
  }

  leaveAnyway(){
    this.userSessionService.clearSession();
    this.userSessionService.data.sessionEnded = true;
    this.authGuardService.cancelLeaveAnyway();
    this.router.navigate(['/instructions']);
  }

  redirect(){
    this.userSessionService.data.companyId = COMPANY_ID.YI;
    this.router.navigate([this.policy.value]);
  }

  onCancelRedirectModal(modalName: string){
    const modalElement =
      this.windowService.nativeDocument.getElementById(modalName);

    if (modalElement && modalElement.close) {
      modalElement.close();
    } else {

      this.modalContentHeading = this.systemErrorContent.title;
      this.modalContentBody = FormUtils.sanitize(this.systemErrorContent.body);

      this.modalService.showModal('prism-ui-modal-redirect-modal'); // Use the service to show the modal

      this.log('Modal with id prism-ui-modal-redirect-modal not found or does not support close method.');
    }
  }

  canDeactivate(){
    // Skip cancel modal when continuing to new application
    if (this.userSessionService.data.isContinuingFromConfirmation) {
      return of(true);
    }
    
    if (!this.isFormSubmitted && 
      !this.userSessionService.data.sessionEnded &&
      this.userSessionService.data.companyId != COMPANY_ID.YI) {

      this.modalContentHeading = this.cancelContent.title;
      this.modalContentBody = FormUtils.sanitize(this.cancelContent.body);
      this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal
      return this.userSessionService.data.sessionEnded;
    } else {
      return of(true);
    }
  }

  agencyLocationDisplay(agencyLocationId: string): string {
    if (!agencyLocationId) return '';
    let index = this.agencyLocations.findIndex(location => location.id === agencyLocationId);
    return this.agencyLocations[index].name;
  }

  locationInputClickEvent(){
    this.display.set("block");
    if(this.policyForm.get('agencyLocation').value === ''){
      this.policyForm.get("agencyLocation").setValue("");
      this.policyForm.get("agencyLocation").updateValueAndValidity();
      this.filteredAgencyLocations.update(values => {
        return [...this.agencyLocations];
      });
      this.display.set("block");
    } else if(this.policyForm.get('agencyLocation').value === undefined){
      return;
    } else {
      let tempAgencyLocations: AgencyLocation[];
      tempAgencyLocations = this.agencyLocations.filter(option => option.name.toLowerCase().includes(this.policyForm.get('agencyLocation').value.toLowerCase()));
      tempAgencyLocations.sort((a, b) => a.name.localeCompare(b.name));
      this.filteredAgencyLocations.update(values => {
        return [...tempAgencyLocations];
      });

      if(this.filteredAgencyLocations().length==0){
        this.display.set("none");
        const selectedOptionName = this.policyForm.get("agencyLocation").value;
        const selectedOption = this.agencyLocations.find(option => option.name === selectedOptionName);
        if(!selectedOption){
          this.policyForm.get("agencyLocation").setErrors({inValidAgencyLocation:true});
          this.policyForm.updateValueAndValidity();
        }else{
          this.policyForm.get("agencyLocation").setErrors({required:true});
          this.policyForm.updateValueAndValidity();
        }
        
      } else{
        this.display.set("block");
      }
    }
  }

  agencyLocationLeaveEvent(event){
    const inputValue = event.target.value;
    this.agencyLocationValidator(inputValue);
  }

  onPolicyNumberInput(event: Event): void {
  const value = (event.target as HTMLInputElement).value;
  if (!value.trim()) {
    this.resetPolicyNumber();
  }
}


  public resetPolicyNumber(){
    this.policyForm.get("policyNumber").setValue(null);
    const policyFormInput = document.getElementById('prism-input-policyNumber') as HTMLInputElement;
    if (policyFormInput) {
      policyFormInput.value = '';
    }
    this.evidenceRequired = true;
    this.ceoiPolicyFlag = false;
    this.policyForm.get("policyNumber").setErrors(null);
    this.policyForm.clearValidators();
    this.policyForm.updateValueAndValidity();
  }

}
